import { ChoiceRow, HIDDEN_INPUT, PEER_FOCUS_RING, choiceAria, choiceIds } from "./ChoiceRow";
import { RadioIndicator, type IndicatorSize, type IndicatorTone } from "./Indicators";
import { useRovingFocus, useRovingTabStop } from "@/hooks/use-roving-focus";
import { cn } from "@/lib/cn";
import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";

type RadioGroupContextValue = {
  name: string;
  value: string | null;
  onValueChange: (value: string) => void;
  disabled: boolean;
  tone: IndicatorTone;
  size: IndicatorSize;
};

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export type RadioProps = Omit<ComponentPropsWithoutRef<"input">, "type" | "size" | "onChange" | "value"> & {
  /** Option value; required inside a RadioGroup. */
  value: string;
  label?: ReactNode;
  description?: ReactNode;
  /** Standalone use only; a RadioGroup decides `checked` from its `value`. */
  onCheckedChange?: (checked: boolean) => void;
  tone?: IndicatorTone;
  size?: IndicatorSize;
};

/** Accessible radio: hidden native input with a RadioIndicator; reads name/value from an enclosing RadioGroup. */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { value, label, description, onCheckedChange, tone, size, className, checked, disabled, name, ...rest },
  ref,
) {
  const group = useContext(RadioGroupContext);
  const ids = choiceIds(useId());
  const isChecked = group ? group.value === value : Boolean(checked);
  const isDisabled = Boolean(disabled || group?.disabled);
  const resolvedSize = size ?? group?.size ?? "md";
  const resolvedTone = tone ?? group?.tone ?? "primary";

  return (
    <ChoiceRow
      size={resolvedSize}
      disabled={isDisabled}
      label={label}
      description={description}
      ids={ids}
      className={className}
      input={
        <input
          {...rest}
          ref={ref}
          type="radio"
          name={group?.name ?? name}
          value={value}
          checked={isChecked}
          disabled={isDisabled}
          {...choiceAria(ids, label, description, rest)}
          onChange={(event) => {
            if (!event.target.checked) return;
            if (group) group.onValueChange(value);
            else onCheckedChange?.(true);
          }}
          className={HIDDEN_INPUT}
        />
      }
      indicator={
        <RadioIndicator
          checked={isChecked}
          tone={resolvedTone}
          size={resolvedSize}
          disabled={isDisabled}
          className={PEER_FOCUS_RING}
        />
      }
    />
  );
});

export type RadioGroupProps = Omit<ComponentPropsWithoutRef<"div">, "onChange" | "defaultValue" | "dir"> & {
  value: string | null;
  onValueChange: (value: string) => void;
  /** Accessible name when no `aria-labelledby` is given. */
  label?: string;
  orientation?: "vertical" | "horizontal";
  name?: string;
  disabled?: boolean;
  tone?: IndicatorTone;
  size?: IndicatorSize;
  children: ReactNode;
};

/** `role="radiogroup"` of `Radio value=` children with roving focus: arrows move and select (wrapping), Home/End jump. */
export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  {
    value,
    onValueChange,
    label,
    orientation = "vertical",
    name,
    disabled = false,
    tone = "primary",
    size = "md",
    className,
    children,
    onKeyDown,
    ...rest
  },
  ref,
) {
  const generatedName = useId();
  const rove = useRovingFocus<HTMLInputElement>({
    selector: 'input[type="radio"]:not(:disabled)',
    onMove: (radio) => {
      if (radio.value !== value) onValueChange(radio.value);
    },
  });
  const groupRef = useRovingTabStop(ref);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);
    rove(event);
  }

  return (
    <RadioGroupContext.Provider
      value={{ name: name ?? generatedName, value, onValueChange, disabled, tone, size }}
    >
      <div
        {...rest}
        ref={groupRef}
        role="radiogroup"
        aria-label={rest["aria-labelledby"] ? undefined : label}
        aria-orientation={orientation}
        aria-disabled={disabled || undefined}
        onKeyDown={handleKeyDown}
        className={cn("flex gap-3", orientation === "vertical" ? "flex-col" : "flex-row flex-wrap", className)}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
});
